import { FOLLOW_UP_NOTE_MAX, FOLLOW_UP_REASON_TEXT, SNOOZE_CHOICES, isIsoDate, shiftDate, validateFollowUpNote, type FollowUpReason } from "@flexfreight/core";
import {
  Banner,
  Button,
  DataTable,
  FilterToolbar,
  KpiTile,
  Modal,
  notify,
  PageHeader,
  SearchInput,
  Select,
  StatusBadge,
  TextAreaField,
  sortRows,
  useConfirm,
  useDebouncedText,
  useUrlFilters,
  type Column,
  type FilterItem,
} from "@flexfreight/ui";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { api } from "../../api/client";
import { carrierName, carrierOptions } from "./Shipments";
import { CustomerLink, day, message, sortOf, sortPatch, time, useCan, when } from "./ops";

type Terminal = { name: string; phone: string; code: string | null; source: "mock" | "live" };
type FollowUp = { note: string; snoozedUntil: string | null; by: string | null; updatedAt: string };
export type BoardCard = {
  id: string;
  orgId: string;
  orgName: string;
  carrier: string;
  status: string;
  bolNumber: string | null;
  proNumber: string | null;
  pickupConfirmation: string | null;
  origin: string;
  destination: string;
  pickupDate: string | null;
  estimatedDeliveryDate: string | null;
  lastTrackedAt: string | null;
  updatedAt: string;
  reasons: FollowUpReason[];
  originTerminal: Terminal | null;
  destinationTerminal: Terminal | null;
  followUp: FollowUp | null;
};
export type Board = {
  date: string;
  asOf: string;
  counts: { pickups: number; pickupsDone: number; exceptions: number; followUps: number; snoozed: number };
  pickups: BoardCard[];
  exceptions: BoardCard[];
  followUps: BoardCard[];
};

const VIEWS = ["followups", "pickups", "exceptions"] as const;
type View = (typeof VIEWS)[number];
const FILTERS = { view: "followups", date: "", q: "", carrier: "", sort: "", dir: "asc" };
/** The dispatcher's calendar day (local), as YYYY-MM-DD. */
const today = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const tel = (phone: string) => {
  const d = phone.replace(/\D/g, "");
  return d.length === 10 ? `tel:+1${d}` : d.length === 11 && d.startsWith("1") ? `tel:+${d}` : null;
};
const snoozeLabel = (h: number) => (h === 0 ? "Keep it in the queue" : h === 1 ? "Snooze 1 hour" : `Snooze ${h} hours`);
const reasonsText = (c: BoardCard) => c.reasons.map((r) => FOLLOW_UP_REASON_TEXT[r]).join(", ");

/** Freight > Dispatch board: today's pickups, open exceptions and the follow-up queue across customers. */
export function DispatchPage() {
  const can = useCan();
  const f = useUrlFilters(FILTERS, useSearchParams());
  const v = f.values;
  const date = isIsoDate(v.date) ? v.date : today();
  const view: View = (VIEWS as readonly string[]).includes(v.view) ? (v.view as View) : "followups";
  const [search, setSearch] = useDebouncedText(v.q, (q) => f.set({ q }));
  const board = useQuery({
    queryKey: ["console", "dispatch", date],
    queryFn: () => api<Board>(`/v1/admin/dispatch?${new URLSearchParams({ date })}`),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
  const [editing, setEditing] = useState<BoardCard | null>(null);
  const b = board.data;
  const list = b ? (view === "pickups" ? b.pickups : view === "exceptions" ? b.exceptions : b.followUps) : [];
  const needle = v.q.toLowerCase();
  const sort = sortOf(v);
  const rows = sortRows(
    list.filter((c) => (!v.carrier || c.carrier === v.carrier) && (!needle || [c.bolNumber, c.proNumber, c.orgName, c.origin, c.destination].some((x) => x?.toLowerCase().includes(needle)))),
    sort,
    (c, k) => (k === "shipment" ? c.bolNumber : k === "customer" ? c.orgName : k === "status" ? c.status : k === "dates" ? time(view === "pickups" ? c.pickupDate : c.estimatedDeliveryDate) : null),
  );
  const writable = can("dispatch:write");
  const count = (x: View) => (b ? ` (${x === "pickups" ? b.counts.pickups : x === "exceptions" ? b.counts.exceptions : b.counts.followUps})` : "");
  const VIEW_LABEL: Record<View, string> = { followups: "Follow-ups", pickups: "Pickups", exceptions: "Exceptions" };
  const items: FilterItem[] = [
    { key: "view", priority: 1, active: view !== "followups", inline: <Select variant="toolbar" label="List" value={view} onChange={(x) => f.set({ view: x })} options={VIEWS.map((x) => ({ value: x, label: `${VIEW_LABEL[x]}${count(x)}` }))} /> },
    { key: "carrier", priority: 2, active: v.carrier !== "", inline: <Select variant="toolbar" label="Carrier" value={v.carrier} onChange={(carrier) => f.set({ carrier })} options={carrierOptions()} /> },
  ];
  const columns: Column<BoardCard>[] = [
    {
      key: "shipment",
      header: "Shipment",
      primary: true,
      sortable: true,
      render: (c) => (
        <>
          <Link className="ffa-rowlink ffa-cell-strong" to={`/shipments/${c.id}`}>{c.bolNumber ?? "No BOL"}</Link>
          <span className="ffa-block ffa-muted">{carrierName(c.carrier)}{c.proNumber ? ` · PRO ${c.proNumber}` : " · No PRO"}</span>
          <span className="ffa-block ffa-muted">{c.origin} → {c.destination}</span>
        </>
      ),
    },
    { key: "customer", header: "Customer", sortable: true, render: (c) => <CustomerLink orgId={c.orgId} name={c.orgName} /> },
    {
      key: "status",
      header: "Status",
      sortable: true,
      width: "11rem",
      render: (c) => (
        <>
          <StatusBadge domain="shipment" value={c.status} />
          {c.reasons.length ? <span className="ffa-block ffa-warn">{reasonsText(c)}</span> : null}
        </>
      ),
    },
    { key: "dates", header: view === "pickups" ? "Pickup" : "ETA", sortable: true, width: "9rem", render: (c) => <>{day(view === "pickups" ? c.pickupDate : c.estimatedDeliveryDate)}<span className="ffa-block ffa-muted">Tracked {c.lastTrackedAt ? when(c.lastTrackedAt) : "never"}</span></> },
    { key: "terminal", header: "Terminal", mobile: "hide", render: (c) => <TerminalCell t={view === "pickups" ? c.originTerminal : (c.destinationTerminal ?? c.originTerminal)} /> },
    {
      key: "follow",
      header: "Follow-up",
      render: (c) => (
        <div className="ffx-cellstack">
          {c.followUp ? (
            <span>
              <span className="ffa-block">{c.followUp.note}</span>
              <span className="ffa-block ffa-muted">{c.followUp.by ?? "Someone"} · {when(c.followUp.updatedAt)}{c.followUp.snoozedUntil && Date.parse(c.followUp.snoozedUntil) > Date.now() ? ` · snoozed until ${when(c.followUp.snoozedUntil)}` : ""}</span>
            </span>
          ) : (
            <span className="ffa-block ffa-muted">Nobody yet</span>
          )}
          {writable && c.status !== "delivered" && c.status !== "cancelled" ? <Button size="sm" onClick={() => setEditing(c)}>{c.followUp ? "Update" : "Claim"}</Button> : null}
        </div>
      ),
    },
  ];
  return (
    <section className="ffa-page">
      <PageHeader
        title="Dispatch board"
        subtitle={`Pickups, exceptions and follow-ups across every customer for ${day(date)}.${b ? ` As of ${when(b.asOf)}.` : ""}`}
        secondaryActions={
          <>
            <Button onClick={() => f.set({ date: shiftDate(date, -1) })}>Previous day</Button>
            <Button onClick={() => f.set({ date: shiftDate(date, 1) })}>Next day</Button>
          </>
        }
        primaryAction={<Button variant="primary" disabled={date === today()} onClick={() => f.set({ date: "" })}>Today</Button>}
      />
      <div className="ffa-kpis" aria-busy={board.isPending || undefined}>
        <KpiTile label="Pickups" value={b ? `${b.counts.pickupsDone}/${b.counts.pickups}` : "–"} sub="picked up" />
        <KpiTile label="Exceptions" value={b ? String(b.counts.exceptions) : "–"} sub="open" tone={b && b.counts.exceptions > 0 ? "danger" : undefined} />
        <KpiTile label="Follow-ups" value={b ? String(b.counts.followUps) : "–"} sub="to chase" tone={b && b.counts.followUps > 0 ? "warning" : undefined} />
        <KpiTile label="Snoozed" value={b ? String(b.counts.snoozed) : "–"} sub="hidden for now" />
      </div>
      <FilterToolbar
        label="Board filters"
        search={<SearchInput label="Search BOL, PRO, customer or city" value={search} onChange={setSearch} />}
        items={items}
        anyActive={v.q !== "" || v.carrier !== "" || view !== "followups"}
        showReset
        onReset={() => {
          setSearch("");
          f.set({ q: "", carrier: "", view: "followups" });
        }}
        meta={b ? `${rows.length} shown` : undefined}
      />
      <DataTable
        caption="Dispatch board"
        columns={columns}
        rows={rows}
        rowKey={(c) => c.id}
        loading={board.isPending || board.isFetching}
        error={board.isError ? { title: "Couldn't load the board", message: message(board.error), onRetry: () => void board.refetch() } : null}
        empty={v.q || v.carrier ? "No loads match these filters." : view === "followups" ? "Nothing to chase. Every moving load is on track." : view === "pickups" ? "No pickups scheduled for this day." : "No open exceptions."}
        rowTone={(c) => (c.status === "exception" ? "danger" : null)}
        sort={sort}
        onSortChange={(s) => f.set(sortPatch(s))}
      />
      {editing ? <FollowUpDialog card={editing} date={date} onClose={() => setEditing(null)} /> : null}
    </section>
  );
}

function TerminalCell({ t }: { t: Terminal | null }) {
  if (!t) return <span className="ffa-muted">-</span>;
  const href = tel(t.phone);
  return (
    <span>
      <span className="ffa-block">{t.name}</span>
      {t.source === "mock" ? <span className="ffa-block"><StatusBadge domain="dataSource" value="mock" size="sm" /></span> : null}
      {href && t.source === "live" ? <a className="ffa-block" href={href}>{t.phone}</a> : <span className="ffa-block ffa-muted">{t.phone}</span>}
    </span>
  );
}

function FollowUpDialog({ card, date, onClose }: { card: BoardCard; date: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [note, setNote] = useState(card.followUp?.note ?? "");
  const [snooze, setSnooze] = useState("0");
  const [error, setError] = useState<string | null>(null);
  const url = `/v1/admin/orgs/${card.orgId}/dispatch/follow-ups/${card.id}`;
  const done = (title: string) => {
    void qc.invalidateQueries({ queryKey: ["console", "dispatch", date] });
    notify.success(title);
    onClose();
  };
  const save = useMutation({
    mutationFn: () => api(url, { method: "PUT", body: { note: note.trim(), ...(Number(snooze) ? { snoozeHours: Number(snooze) } : {}) } }),
    onSuccess: () => done(Number(snooze) ? "Follow-up snoozed" : "You're on it"),
    onError: (e) => notify.error("Not saved", message(e)),
  });
  const clear = useMutation({
    mutationFn: () => api(url, { method: "DELETE" }),
    onSuccess: () => done("Follow-up cleared"),
    onError: (e) => notify.error("Not cleared", message(e)),
  });
  const submit = () => {
    const err = validateFollowUpNote(note);
    setError(err);
    if (!err) save.mutate();
  };
  return (
    <Modal
      open
      onClose={onClose}
      title={`${card.followUp ? "Update" : "Claim"} ${card.bolNumber ?? "shipment"}`}
      description={`${card.orgName} · ${carrierName(card.carrier)} · ${card.origin} → ${card.destination}`}
      footer={
        <>
          {card.followUp ? (
            <Button
              variant="ghost"
              loading={clear.isPending}
              onClick={async () => {
                if (await confirm({ title: "Clear this follow-up?", body: "The note and any snooze are removed, and the shipment shows as unclaimed again.", confirmLabel: "Clear", danger: true })) clear.mutate();
              }}
            >
              Clear
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={save.isPending} onClick={submit}>Save</Button>
        </>
      }
    >
      <div className="ffa-form">
        {card.reasons.length ? <Banner tone="warning">Needs attention: {reasonsText(card)}</Banner> : null}
        <TextAreaField label="What's happening" value={note} onChange={(e) => setNote(e.target.value)} rows={3} maxLength={FOLLOW_UP_NOTE_MAX} error={error ?? undefined} hint="The whole team sees this on the board." />
        <Select label="Snooze" value={snooze} onChange={setSnooze} options={SNOOZE_CHOICES.map((h) => ({ value: String(h), label: snoozeLabel(h) }))} />
        <p className="ffa-small ffa-muted">This records who is chasing the load. It doesn&apos;t contact the carrier.</p>
      </div>
      {dialog}
    </Modal>
  );
}
